import { Box, SelectChangeEvent } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

import { useCallback, useContext, useMemo } from 'react';
import { Spinner } from '@/components/common/Spinner';
import SelectApplicationCycle from '@/components/dashboard_components/SelectApplicationCycle';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { ApplicationDataGridRowProps } from '@/models/Application';
import { UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';
import { addEvenOddRowClassName, GridLocaleTextOverride } from '@/utils/dataGridUtil';

import ApplicationDataGridDetails from './ApplicationDataGridDetails';
import {
  DataGridPremium,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
  GridToolbarContainerProps,
} from '@mui/x-data-grid-premium';
import { LocaleContext } from '@/Contexts/LocaleContext';

interface HistoricalApplicationDataGridProps {
  dataGridSx: SxProps<Theme>;
  columns: GridColDef[];
  loading: boolean;
  customExportTool: (props: GridToolbarContainerProps) => JSX.Element;
  onNoRowsOverlay?: (loading: boolean) => JSX.Element;
  onNoResultsOverlay?: () => JSX.Element;
  onFilterModelChange?: (f: GridFilterModel) => void;
  onSortModelChange?: (s: GridSortModel) => void;
  onPaginationModelChange?: (p: GridPaginationModel) => void;
}

export default function HistoricalApplicationDataGrid({
  dataGridSx,
  columns,
  loading,
  customExportTool,
  onNoRowsOverlay,
  onNoResultsOverlay,
  onFilterModelChange,
  onSortModelChange,
  onPaginationModelChange,
}: HistoricalApplicationDataGridProps) {
  const { userCurrent } = useContext(UsersContext);

  const {
    historicApps,
    historicApplicationSortModel,
    historicApplicationFilterModel,
    historicApplicationPaginationModel,
    historicApplicationYearSelected,
    columnVisibilityModel,
    updateHistoricApps,
    updateHistoricApplicationSortModel,
    updateHistoricApplicationFilterModel,
    updateHistoricApplicationPaginationModel,
    updateHistoricApplicationYearSelected,
  } = useContext(DashboardContext);
  const { locale } = useContext(LocaleContext);

  const handleFilterModelChange = useCallback(
    (f: GridFilterModel) => {
      if (onFilterModelChange != null) {
        onFilterModelChange(f);
        return;
      }

      updateHistoricApplicationFilterModel(f);
    },
    [onFilterModelChange, updateHistoricApplicationFilterModel]
  );

  const handleSortModelChange = useCallback(
    (s: GridSortModel) => {
      if (onSortModelChange != null) {
        onSortModelChange(s);
        return;
      }

      updateHistoricApplicationSortModel(s);
    },
    [onSortModelChange, updateHistoricApplicationSortModel]
  );

  const handlePaginationModelChange = useCallback(
    (p: GridPaginationModel) => {
      if (onPaginationModelChange != null) {
        onPaginationModelChange(p);
        return;
      }

      updateHistoricApplicationPaginationModel(p);
    },
    [onPaginationModelChange, updateHistoricApplicationPaginationModel]
  );

  const handleRowUpdate = useCallback(
    (updatedRow: ApplicationDataGridRowProps) => {
      const updatedApplications = historicApps.map((app) => {
        if (app.applicationId === updatedRow.applicationId) {
          return updatedRow;
        }
        return app;
      });

      updateHistoricApps(updatedApplications);
    },
    [historicApps, updateHistoricApps]
  );

  const failedRows = useMemo(
    () =>
      historicApps.filter(
        (app) =>
          app.failedApplicationTransactions != null && app.failedApplicationTransactions.length > 0
      ),
    [historicApps]
  );

  const nonFailedRows = useMemo(
    () =>
      historicApps.filter(
        (app) =>
          app.failedApplicationTransactions == null ||
          app.failedApplicationTransactions.length === 0
      ),
    [historicApps]
  );

  const prioritizedRows = useMemo(
    () => [...failedRows, ...nonFailedRows],
    [failedRows, nonFailedRows]
  );

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        height: '100%',
      }}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          flex: '1 0 auto',
        }}
      >
        <SelectApplicationCycle
          includeNoValueSelection
          value={historicApplicationYearSelected}
          onlyShowAppCyclesWithApplications
          onChange={(e: SelectChangeEvent<string>) => {
            const selectedYear = e.target.value;
            updateHistoricApplicationYearSelected(selectedYear);
          }}
        />
      </Box>

      {historicApps != null ? (
        <DataGridPremium
          showToolbar
          localeText={GridLocaleTextOverride(locale)}
          getRowId={(row: ApplicationDataGridRowProps) => row?.applicationId}
          getRowClassName={addEvenOddRowClassName((params) => {
            const currentRow = params.row as ApplicationDataGridRowProps;
            return currentRow.status !== ApplicationStatus.Closed &&
              currentRow.failedApplicationTransactions != null &&
              currentRow.failedApplicationTransactions.length > 0 &&
              userCurrent?.userType === UserType.Adf
              ? 'bc-error'
              : '';
          })}
          getDetailPanelContent={(params: GridRowParams<ApplicationDataGridRowProps>) => {
            const currentRow = params.row;
            if (currentRow.applicationId == null) return <></>;

            return currentRow != null ? (
              <ApplicationDataGridDetails applicationRow={params} updateRow={handleRowUpdate} />
            ) : (
              <> </>
            );
          }}
          getDetailPanelHeight={() => 'auto'}
          disableVirtualization
          sx={dataGridSx}
          initialState={{ density: 'compact' }}
          disableDensitySelector
          pagination
          paginationModel={historicApplicationPaginationModel}
          onPaginationModelChange={(p: GridPaginationModel) => handlePaginationModelChange(p)}
          filterModel={
            historicApplicationFilterModel != null ? historicApplicationFilterModel : { items: [] }
          }
          onFilterModelChange={(f: GridFilterModel) => handleFilterModelChange(f)}
          sortModel={historicApplicationSortModel != null ? historicApplicationSortModel : []}
          onSortModelChange={(s: GridSortModel) => handleSortModelChange(s)}
          columnVisibilityModel={columnVisibilityModel}
          rows={prioritizedRows}
          columns={columns}
          disableColumnResize
          disableRowSelectionOnClick
          slots={{
            noRowsOverlay: () => (onNoRowsOverlay != null ? onNoRowsOverlay(loading) : <></>),
            noResultsOverlay: () => (onNoResultsOverlay != null ? onNoResultsOverlay() : <></>),
            toolbar: customExportTool,
          }}
        />
      ) : (
        <Spinner />
      )}
    </Box>
  );
}
